/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-param-reassign */
import window from 'global/window';
import { load } from 'cheerio';
import logger from './logger';
import {
  ENVIRONMENT_CONSTANTS,
  PRELOADED_STATE_KEY,
  AWS_ENV_VARS,
} from './constants';
import { makeSingleLangTranslatedData } from '../utils/srp.util';
import getTranslatedText from '../services/translate.service';
import { DEFAULT_LANGUAGE } from '../constants/app-constants';
import LOCALES from '../constants/locale-constants';

export const getEnvironmentConstant = ({ key, env }) => {
  if (ENVIRONMENT_CONSTANTS[env] && ENVIRONMENT_CONSTANTS[env][key]) {
    const defaultValue = ENVIRONMENT_CONSTANTS[env][key];
    if (typeof window[PRELOADED_STATE_KEY]?.envData?.[key] !== 'undefined') {
      return window[PRELOADED_STATE_KEY]?.envData[key];
    }
    if (typeof process.env[key] !== 'undefined') {
      // Add Validation
      if (AWS_ENV_VARS[key]) {
        const processEnv = `${process.env[key]}`;
        if (AWS_ENV_VARS[key].default || processEnv) { // If not set and no default available hide the key
          if (typeof AWS_ENV_VARS[key].validator === 'function') {
            return AWS_ENV_VARS[key].validator(processEnv) ? processEnv : AWS_ENV_VARS[key].default;
          }
          return processEnv || AWS_ENV_VARS[key].default;
        }
      }
      return `${process.env[key]}`;
    }
    return defaultValue;
  }
  logger.error(`${key} not found in environment constants config for env ${env}`);
  return null;
};

export const stripTrailingSlash = (str) => (str.endsWith('/') ? str.slice(0, -1) : str);

// Flatten array of arrays
export const flatDeep = (arr, d = 1) => (d > 0
  ? arr.reduce((acc, val) => acc.concat(Array.isArray(val)
    ? flatDeep(val, d - 1) : val), [])
  : arr.slice());

// Generate URN for LRS Producer API
export const generateURN = ({
  uid, verb, lrsId, type,
}) => `${uid}:${verb}:urn:${type}:${lrsId}`;

export const getInnerText = (obj) => {
  if (obj?.props?.children) {
    if (Array.isArray(obj.props.children)) {
      const map = obj.props.children.map((child) => {
        if (child?.props?.children) {
          return getInnerText(child);
        }
        if (typeof child === 'string') {
          return child;
        }
        // If child is an object without string children eg <br/> then return empty string
        return '';
      });
      return map.filter((str) => str !== '').join(' ');
    }
    return obj.props.children;
  }
  return obj;
};

export const getDynamicTranslations = async (textsToTranslate, language) => {
  const translateDataArr = [];
  let activeChunk = [];
  let currentSize = 0;
  textsToTranslate.forEach((text) => {
    currentSize += text.source.length;
    if (currentSize > 5000) {
      translateDataArr.push(activeChunk);
      activeChunk = [text];
      currentSize = text.source.length;
    } else {
      activeChunk.push(text);
    }
  });
  if (activeChunk.length > 0) {
    translateDataArr.push(activeChunk);
  }
  let data = [];
  let payload = [];
  const promises = translateDataArr.map(async (translateDataChunk) => {
    const response = await getTranslatedText(translateDataChunk, language);
    data = [...data, ...response];
    payload = [...payload, ...translateDataChunk];
  });
  await Promise.all(promises);
  return makeSingleLangTranslatedData(data, payload, language);
};

export const getTextFromElement = (text = '') => {
  if (typeof DOMParser === 'undefined') {
    const $ = load(text);
    const textContent = $('body').text() || '';
    return textContent.trim();
  }
  const parser = new DOMParser();
  const parsedText = parser.parseFromString(text, 'text/html');
  const textContent = parsedText.body.textContent || parsedText.body.innerText || '';
  return textContent.trim();
};

export const modifyPartnerListing = (listing) => {
  const modifiedListing = { ...listing };
  if (!modifiedListing?.data?.partners?.length) {
    return listing;
  }
  modifiedListing.data.partners.forEach((element) => {
    element.headline = getTextFromElement(element.headline);
  });
  return modifiedListing;
};

export const modifyPartnerDetails = (details) => {
  const response = { ...details };
  response?.data?.success_stories?.forEach((element) => {
    element.customer_challenge = getTextFromElement(element.customer_challenge);
  });
  if (response?.data?.headline) {
    response.data.headline = getTextFromElement(response.data.headline);
  }
  return response;
};

window.getInnerText = getInnerText;

export const getCurrentPathName = (location) => {
  const currentUrl = location.pathname; // Get current path

  // For Chinese locale (domain-based), don't remove any segments
  // For other locales, keep their URL structure intact
  // This function should only be called in specific redirect scenarios

  // Get the query parameters
  const queryParams = location.search;
  // Return the URL as-is - no automatic segment removal
  return `${currentUrl}${queryParams}`;
};
const getCookie = (cName, cookieContext) => {
  if (cookieContext !== undefined) {
    const ca = cookieContext?.split(';');
    let cookieValue = '';
    ca.forEach((cookie) => {
      const c = cookie.replace(/^\s+|\s+$/gm, '');
      if (c.indexOf(cName) !== -1) cookieValue = c.substring(cName.length + 1, c.length);
    });
    return cookieValue;
  }
  return '';
};
const hasCnInDomain = (hostname) => {
  try {
    return hostname.endsWith('.cn');
  } catch (e) {
    return false; // If the URL is invalid
  }
};

export const getLocaleFromPath = (lang, hostname, requestHeaders = {}) => {
  const cookieOrigin = requestHeaders.cookie ? getCookie('origin-country', requestHeaders.cookie) : '';
  const isChinese = (cookieOrigin === 'china' || hasCnInDomain(hostname));
  console.log('isChinese page -> ', isChinese, 'lang -> ', lang, 'hostname -> ', hostname, 'requestHeaders -> ', requestHeaders);
  const locale = isChinese ? LOCALES.cn : LOCALES[lang ?? 'en'] || LOCALES.en;
  return locale;
};

export const getBaseURL = (locale, path) => {
  const hostname = window?.location?.hostname || '';
  const localeKey = getLocaleFromPath(locale, hostname, {});
  let url = path;
  if (localeKey !== DEFAULT_LANGUAGE && !hostname.endsWith('.com.cn')) {
    url = `/${locale}${path}`;
  }
  return url;
};

/** Full page navigation (same origin) so CDN can route e.g. /support/partners outside this SPA. */
export const assignBaseURL = (locale, path) => {
  if (typeof window?.location?.assign !== 'function') {
    return;
  }
  window.location.assign(getBaseURL(locale, path));
};
